import { useEffect, useRef, useState } from "react";
import { Mic } from "lucide-react";
import { useTranslation } from "react-i18next";

const LANG_MAP = { ro: "ro-RO", hu: "hu-HU", en: "en-US" };

/**
 * Voice-to-text button using the Web Speech API.
 * Built for OXO's field users (dirty hands / gloves / noisy workshops).
 * Renders nothing if the browser has no speech recognition support.
 *
 * Props:
 *  - onInterim(text): live partial transcript (optional)
 *  - onResult(text): final transcript
 *  - size: icon size class (default w-5 h-5)
 *  - className / btnClass: styling hooks
 */
export default function MicButton({ onInterim, onResult, size = "w-5 h-5", btnClass = "", testid = "mic-button" }) {
  const { i18n } = useTranslation();
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const recogRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSupported(!!SR);
    return () => { try { recogRef.current?.stop(); } catch { /* noop */ } };
  }, []);

  const toggle = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    if (listening) { try { recogRef.current?.stop(); } catch { /* noop */ } return; }

    const recog = new SR();
    recog.lang = LANG_MAP[i18n.language] || "ro-RO";
    recog.interimResults = true;
    recog.continuous = false;
    recog.maxAlternatives = 1;

    recog.onstart = () => setListening(true);
    recog.onend = () => setListening(false);
    recog.onerror = () => setListening(false);
    recog.onresult = (event) => {
      let interim = "", final = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const chunk = event.results[i][0].transcript;
        if (event.results[i].isFinal) final += chunk;
        else interim += chunk;
      }
      if (interim && onInterim) onInterim(interim);
      if (final && onResult) onResult(final.trim());
    };
    recogRef.current = recog;
    try { recog.start(); } catch { /* noop */ }
  };

  if (!supported) return null;

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={listening ? "Se ascultă..." : "Căutare vocală"}
      title={listening ? "Se ascultă..." : "Vorbește (voice-to-text)"}
      data-testid={testid}
      className={`inline-flex items-center justify-center transition-colors ${
        listening ? "text-red-600" : "text-slate-400 hover:text-orange-600"
      } ${btnClass}`}
    >
      <span className="relative flex items-center justify-center">
        {listening && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-60" />}
        <Mic className={`relative ${size}`} />
      </span>
    </button>
  );
}
